/**
 * A project's OWN data-copy classification, additive to the template's
 * (`src/core/system/data-copy.ts`) — never edit that file to add a project
 * table. See `docs/DATA-COPY.md`.
 *
 * MAJIK (docs/majik/DATA-MODEL-V1.md §2). For now nothing personal of
 * Majik's leaves production: every table that holds a person, a business, an
 * address, a note or a message is `drop`, window `none`. Only the catalog —
 * products and work types — is copied. Three things have to change before a
 * fuller copy is safe:
 *
 *  1. The activity log. `record_change` keeps a row about a client, contact,
 *     job… whenever that table is not entirely `drop`, and the row carries the
 *     record's title (a person's or business's name) and its before/after
 *     values. Faking the table's columns would still leak them there.
 *  2. Order. The refresh deletes and inserts tables in registry order;
 *     Majik's foreign keys are RESTRICT, so a second refresh could not delete
 *     a client while its locations remain.
 *  3. Required text. An address or a job name is NOT NULL with no default;
 *     `drop` omits it and the insert fails. It needs a fake kind for places.
 *
 * Then the intended classes (DATA-MODEL-V1 §10, row B) apply: names, methods,
 * addresses, notes and message bodies `fake`/`drop`; money, statuses and dates
 * `keep`.
 */
import type { ColumnClassification, TableClassification } from './data-copy.ts'

const keep: ColumnClassification = { class: 'keep' }
const drop: ColumnClassification = { class: 'drop' }

const PRIVATE_FOR_NOW =
  'Majik’s records: people, businesses, addresses, notes and messages. Not copied until the activity log, the refresh order and required text are handled (see the top of this file).'
const IMPORT =
  'The Monday import’s bookkeeping: staged cells, snapshots and review lists hold real names and numbers. Never copied — the sandbox imports nothing (DATA-MODEL-V1 §7.1).'

/** Every column dropped: the table is never copied. */
function never(table: string, columns: readonly string[], why: string): TableClassification {
  return {
    table,
    window: { kind: 'none' },
    columns: Object.fromEntries(columns.map((column) => [column, drop])),
    why,
  }
}

const MAIN = ['id', 'organizationId', 'createdAt', 'updatedAt', 'createdBy', 'deletedAt'] as const

export const projectDataCopyTables: TableClassification[] = [
  // The catalog: what Majik sells and the kinds of work it does. No person in it.
  {
    table: 'products',
    window: { kind: 'all' },
    columns: {
      ...Object.fromEntries(MAIN.map((column) => [column, keep])),
      createdBy: drop, // a real user id; no accounts are copied
      name: keep,
      description: keep,
      category: keep,
      type: keep,
      unitPrice: keep,
      unitCost: keep,
      stock: keep,
      taxCode: keep,
      status: keep,
      quickbooksItemName: keep,
      quickbooksItemId: keep,
    },
    why: 'Majik’s product and service catalog: names, prices and stock, nothing personal.',
  },
  {
    table: 'work_types',
    window: { kind: 'all' },
    columns: {
      id: keep,
      organizationId: keep,
      name: keep,
      active: keep,
      sortOrder: keep,
      deletedAt: keep,
    },
    why: 'The kinds of work Majik does — a lookup with nothing personal in it.',
  },

  never(
    'business_rules',
    [
      'organizationId',
      'timeZone',
      'officePhone',
      'officeTextNumber',
      'noticeHour',
      'cleanerListHour',
      'salesTaxRate',
      'overdueAfterDays',
      'paymentReminderDays',
      'goLive',
      'goLiveAt',
      'goLiveBy',
      'jobNoticeTemplate',
      'textSignature',
      'reengageAfterDays',
      'lowStockAt',
      'goLiveChecks',
      'updatedAt',
    ],
    'Majik’s business rules, including the office numbers and who switched Go live on. The sandbox keeps its own (Go live is always off there).',
  ),
  never('team', ['id', 'organizationId', 'name'], PRIVATE_FOR_NOW),
  never(
    'team_member',
    ['organizationId', 'teamId', 'userId'],
    'Which account is in which team — accounts are never copied.',
  ),
  never('team_alert', ['organizationId', 'teamId', 'alertKind'], PRIVATE_FOR_NOW),
  never(
    'contacts',
    [
      ...MAIN,
      'firstName',
      'lastName',
      'language',
      'textsBlocked',
      'textsBlockedAt',
      'isInspector',
      'isStaff',
      'notes',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'contact_methods',
    [
      'id',
      'organizationId',
      'contactId',
      'kind',
      'value',
      'extension',
      'label',
      'isPrimary',
      'isCurrent',
      'source',
      'createdAt',
    ],
    'Every phone number and email address of every person Majik deals with. Never copied.',
  ),
  never(
    'clients',
    [
      ...MAIN,
      'name',
      'taxCode',
      'paymentTerms',
      'billingMethod',
      'quickbooksCustomerId',
      'billingAddress',
      'notes',
      'paymentRemindersPaused',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'locations',
    [
      ...MAIN,
      'clientId',
      'name',
      'status',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'postalCode',
      'workNotes',
      'accessNotes',
      'serviceFrequency',
      'nextBillDate',
      'billingCadence',
      'remindersEnabled',
      'lastReminderOn',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'client_contacts',
    [
      'id',
      'organizationId',
      'clientId',
      'locationId',
      'contactId',
      'role',
      'isPrimary',
      'deletedAt',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'billing_items',
    [
      'id',
      'organizationId',
      'clientId',
      'locationId',
      'productId',
      'itemName',
      'description',
      'unitPrice',
      'quantity',
      'lineTotal',
      'active',
      'sortOrder',
    ],
    PRIVATE_FOR_NOW,
  ),
  never('cleaners', [...MAIN, 'contactId', 'status', 'shift', 'notifyByText'], PRIVATE_FOR_NOW),
  never(
    'cleaner_time_off',
    [
      'id',
      'organizationId',
      'cleanerId',
      'startDate',
      'endDate',
      'allDay',
      'startTime',
      'endTime',
      'reason',
      'note',
      'createdAt',
      'updatedAt',
      'deletedAt',
    ],
    'When a cleaner is away and why (sick, out of town) — personal. Never copied.',
  ),
  never(
    'recurring_jobs',
    [
      ...MAIN,
      'locationId',
      'name',
      'status',
      'cadence',
      'startsOn',
      'startTime',
      'endDate',
      'leadDays',
      'materialisedThrough',
      'workTypeId',
      'hours',
      'billAmount',
      'notes',
      'notifyCustomer',
      'notifyCleaners',
      'splitFromId',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'recurring_job_cleaners',
    ['organizationId', 'recurringJobId', 'cleanerId', 'isLead', 'position'],
    PRIVATE_FOR_NOW,
  ),
  never(
    'jobs',
    [
      ...MAIN,
      'locationId',
      'recurringJobId',
      'occurrenceOn',
      'name',
      'status',
      'scheduledAt',
      'startTime',
      'endTime',
      'hours',
      'billAmount',
      'notes',
      'notifyCustomer',
      'notifyCleaners',
      'completedAt',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'job_cleaners',
    ['organizationId', 'jobId', 'cleanerId', 'isLead', 'position'],
    PRIVATE_FOR_NOW,
  ),
  never('job_work_types', ['organizationId', 'jobId', 'workTypeId'], PRIVATE_FOR_NOW),
  never(
    'invoices',
    [
      ...MAIN,
      'clientId',
      'locationId',
      'name',
      'type',
      'billingMonth',
      'status',
      'issuedOn',
      'dueDate',
      'taxCode',
      'paymentTerms',
      'readyToSendAt',
      'lastSentAt',
      'paidAt',
      'closedAt',
      'statusReason',
      'quickbooksInvoiceId',
      'invoiceNumber',
      'quickbooksTotal',
      'balanceDue',
      'notes',
      'importedUnpaidStale',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'invoice_lines',
    [
      'id',
      'organizationId',
      'invoiceId',
      'itemName',
      'description',
      'unitPrice',
      'quantity',
      'lineTotal',
      'jobId',
      'orderLineId',
      'billingItemId',
      'productId',
      'sortOrder',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'orders',
    [...MAIN, 'locationId', 'name', 'status', 'orderedOn', 'deliveredOn', 'acceptedBy', 'notes'],
    PRIVATE_FOR_NOW,
  ),
  never(
    'order_lines',
    [
      'id',
      'organizationId',
      'orderId',
      'productId',
      'itemName',
      'description',
      'unitPrice',
      'quantity',
      'lineTotal',
      'allocatedAt',
      'sortOrder',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'inspections',
    [...MAIN, 'inspectorContactId', 'status', 'notes', 'sentAt'],
    PRIVATE_FOR_NOW,
  ),
  never(
    'inspection_locations',
    [
      'organizationId',
      'inspectionId',
      'locationId',
      'cleanerId',
      'jobId',
      'cleanedOn',
      'notes',
      'sortOrder',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'communications',
    [
      ...MAIN,
      'channel',
      'direction',
      'purpose',
      'status',
      'title',
      'subject',
      'body',
      'partyKind',
      'partyContactId',
      'partyName',
      'note',
      'at',
      'readAt',
      'authorUserId',
      'authorName',
      'editedAt',
      'editedByName',
      'parentId',
    ],
    'Every text, email and note: what was said to whom. Never copied.',
  ),
  never(
    'communication_links',
    [
      'id',
      'organizationId',
      'communicationId',
      'jobId',
      'clientId',
      'locationId',
      'invoiceId',
      'cleanerId',
      'contactId',
      'orderId',
      'inspectionId',
    ],
    PRIVATE_FOR_NOW,
  ),
  never(
    'monday_import_run',
    [
      'id',
      'organizationId',
      'startedBy',
      'mode',
      'status',
      'files',
      'counts',
      'report',
      'error',
      'createdAt',
      'finishedAt',
    ],
    IMPORT,
  ),
  never(
    'monday_import_row',
    [
      'organizationId',
      'runId',
      'board',
      'sourceKey',
      'kind',
      'parentKey',
      'rowNumber',
      'cells',
      'createdAt',
    ],
    IMPORT,
  ),
  never(
    'monday_record',
    [
      'organizationId',
      'board',
      'sourceKey',
      'mondayItemId',
      'entity',
      'recordId',
      'snapshot',
      'firstRunId',
      'lastRunId',
      'lastImportedAt',
    ],
    IMPORT,
  ),
  never(
    'monday_reference_map',
    ['organizationId', 'board', 'reference', 'entity', 'recordId', 'decidedBy', 'decidedAt'],
    IMPORT,
  ),
]
